import apiClient from "@/services/api-client";
import type { ReadinessProgress } from "@medicorepay/shared";
import { productRevivalApi } from "@/services/product-revival-client";
import type {
  PlanSummary,
  StoreUpdatePayload,
  SubscriptionCreatePayload,
  TenantDetail,
  TenantListQuery,
  TenantListResponse,
  TenantOnboardingPayload,
  TenantUpdatePayload,
} from "@/types";

function idempotencyKey() {
  return crypto.randomUUID();
}

export type TenantReadiness = ReadinessProgress;

export const tenantService = {
  list: async (params: TenantListQuery) => {
    const response = await apiClient.get<TenantListResponse>("/tenants", {
      params,
    });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await apiClient.get<TenantDetail>(`/tenants/${id}`);
    return response.data;
  },
  getReadiness: async (tenantId: string, storeId?: string) => {
    return productRevivalApi.getTenantReadiness(tenantId, storeId);
  },
  update: async (id: string, payload: TenantUpdatePayload) => {
    const response = await apiClient.patch<TenantDetail>(
      `/tenants/${id}`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
  onboard: async (payload: TenantOnboardingPayload, idempotencyKey: string) => {
    const response = await apiClient.post<TenantDetail>(
      "/tenants/onboard",
      payload,
      { headers: { "Idempotency-Key": idempotencyKey } },
    );
    return response.data;
  },
  listPlans: async () => {
    const response = await apiClient.get<PlanSummary[]>("/plans");
    return response.data;
  },
  createSubscription: async (
    tenantId: string,
    payload: SubscriptionCreatePayload,
  ) => {
    const response = await apiClient.post(
      `/tenants/${tenantId}/subscriptions`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
  updateStore: async (
    tenantId: string,
    storeId: string,
    payload: StoreUpdatePayload,
  ) => {
    const response = await apiClient.patch(
      `/tenants/${tenantId}/stores/${storeId}`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
  updatePaymentMethod: async (
    tenantId: string,
    storeId: string,
    method: string,
    payload: { isEnabled?: boolean },
  ) => {
    const response = await apiClient.patch(
      `/tenants/${tenantId}/stores/${storeId}/payment-methods/${method}`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
  updateBillingPrinter: async (
    tenantId: string,
    storeId: string,
    payload: {
      name?: string;
      printerType?: string;
      connectionType?: string;
      isActive?: boolean;
    },
  ) => {
    const response = await apiClient.patch(
      `/tenants/${tenantId}/stores/${storeId}/billing-printer`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
  forceLogout: async (tenantId: string) => {
    const response = await apiClient.post<{
      message: string;
      revokedCount: number;
      usersAffected: number;
    }>(`/tenants/${tenantId}/force-logout`, undefined, {
      headers: { "Idempotency-Key": idempotencyKey() },
    });
    return response.data;
  },
};
